import { Header, Select, Button, Input } from '@scuf/common';
import i18n from 'i18n';
import { FC, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { bslbrCountriesAtom } from 'resources/atoms/bslbrAtom';
import { CalendarDay, HolidayDTO } from 'models/bslbr/general';
import { BSLBR, notificationAtom } from 'resources';
import useFetch from 'helpers/hooks/useFetch';

interface AddHolidayProps {
    onSave: () => void;
    onBack: () => void;
}

const AddHoliday: FC<AddHolidayProps> = ({ onBack }) => {
    const [countries, setCountries] = useRecoilState(bslbrCountriesAtom);
    const [country, setCountry] = useState<string>('');
    const [holidayDescription, setHolidayDescription] = useState<string>('');
    const [holidays, setHolidays] = useState<HolidayDTO[]>([]);
    const [respHolidays, setRespHolidays] = useState<HolidayDTO[]>([]);
    const setNotification = useSetRecoilState(notificationAtom);

    const { t } = useTranslation(['common', 'bslbr', 'user', 'pages']);

    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [selectedDate, setSelectedDate] = useState<string>('');

    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    const monthName = currentDate.toLocaleDateString(i18n.language, {
        month: 'long',
        year: 'numeric'
    });

    const [, createHolidays, loadingCreating] = useFetch<HolidayDTO[], any>({
        url: BSLBR.postHoliday,
        method: 'POST',
        onError: t('notification:critical.Create')
    });

    const [, getHolidaysByCountry, loadingGetAllHolidays] = useFetch<{ country: string }, HolidayDTO[]>({
        url: BSLBR.getAllHolidaysByCountry,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        if (country != null && country != '') getAllHolidaysByCountry(country);
    }, [country]);

    const formatDate = (year: number, month: number, day: number) => {
        const formattedMonth = String(month + 1).padStart(2, '0');
        const formattedDay = String(day).padStart(2, '0');

        return `${year}-${formattedMonth}-${formattedDay}`;
    };

    const normalizeDate = (date?: string) => {
        if (!date) return '';

        if (date.includes('-')) {
            return date.substring(0, 10);
        }

        if (date.includes('/')) {
            const [day, month, year] = date.split('/');

            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        return date;
    };

    const calendarDays = useMemo<CalendarDay[]>(() => {
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

        const firstWeekDay = firstDayOfMonth.getDay();
        const totalDaysInMonth = lastDayOfMonth.getDate();

        const days: CalendarDay[] = [];

        for (let i = 0; i < firstWeekDay; i++) {
            days.push({
                day: 0,
                date: '',
                isCurrentMonth: false
            });
        }

        for (let day = 1; day <= totalDaysInMonth; day++) {
            days.push({
                day,
                date: formatDate(currentYear, currentMonth, day),
                isCurrentMonth: true
            });
        }

        return days;
    }, [currentYear, currentMonth]);

    const goToPreviousMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
    };

    const handleSelectDay = (date: string) => {
        if (!country) return;

        setSelectedDate(date);
        setHolidayDescription('');
    };

    const handleAddHoliday = () => {
        if (!country || !selectedDate || !holidayDescription.trim()) return;

        const alreadyExists =
            holidays.some(holiday => holiday.Data === selectedDate && holiday.Country === country) ||
            respHolidays.some(holiday => holiday.Data === selectedDate && holiday.Country === country);

        if (alreadyExists) return;

        const newHoliday: HolidayDTO = {
            Id: 0,
            Data: selectedDate,
            Description: holidayDescription.trim(),
            Country: country
        };

        setHolidays(current => [...current, newHoliday]);
        setSelectedDate('');
        setHolidayDescription('');
    };

    const isHolidayAlreadyAdded = (date: string) => {
        const normalizedDate = normalizeDate(date);

        return holidays.some(holiday => normalizeDate(holiday.Data) === normalizedDate && holiday.Country === country);
    };

    const isHolidayAlreadyExist = (date: string) => {
        const normalizedDate = normalizeDate(date);

        return respHolidays.some(holiday => normalizeDate(holiday.Data) === normalizedDate && holiday.Country === country);
    };

    const saveHoliday = async () => {
        try {
            var resp = await createHolidays(holidays);
            setNotification(old => ({ ...old, message: t('bslbr:translation.HolidaysAddSuccess'), type: 'success' }));
            setHolidays([]);
            setHolidayDescription('');
            getAllHolidaysByCountry(country);
        } catch {
            setNotification(old => ({ ...old, message: t('bslbr:translation.HolidaysAddError'), type: 'critical' }));
        }
    };

    const getAllHolidaysByCountry = async (country: string) => {
        await getHolidaysByCountry({ country }, (current: any) => {
            if (current.data) {
                setRespHolidays(current.data);
            }
        });
    };

    const calendarHoliday = () => {
        return (
            <div>
                <Header title={t('pages:holiday')} logo={false} menu={false}></Header>
                <div className="form4Columns">
                    <Select
                        placeholder={t('user:address.Country')}
                        label={t('user:address.Country')}
                        value={country}
                        options={countries}
                        search={true}
                        onChange={value => setCountry(value)}
                    />
                </div>
                <div className="holiday-register-content">
                    <div className="holiday-calendar-container">
                        <div className="holiday-calendar-card">
                            <div className="holiday-calendar-header">
                                <Button type="secondary" content="<" onClick={goToPreviousMonth} />

                                <h2>{monthName}</h2>

                                <Button type="secondary" content=">" onClick={goToNextMonth} />
                            </div>

                            <div className="holiday-calendar-weekdays">
                                <span>{t('common:calendar.Sunday')}</span>
                                <span>{t('common:calendar.Mondar')}</span>
                                <span>{t('common:calendar.Tuesday')}</span>
                                <span>{t('common:calendar.Wednesday')}</span>
                                <span>{t('common:calendar.Thursday')}</span>
                                <span>{t('common:calendar.Friday')}</span>
                                <span>{t('common:calendar.Saturday')}</span>
                            </div>

                            <div className="holiday-calendar-grid">
                                {calendarDays.map((item, index) => {
                                    const alreadyAdded = item.date ? isHolidayAlreadyAdded(item.date) : false;
                                    const alreadyExist = item.date ? isHolidayAlreadyExist(item.date) : false;

                                    return (
                                        <button
                                            key={`${item.date}-${index}`}
                                            type="button"
                                            disabled={
                                                !item.isCurrentMonth ||
                                                !country ||
                                                alreadyAdded ||
                                                loadingGetAllHolidays ||
                                                alreadyExist
                                            }
                                            className={`
                                                holiday-calendar-day
                                                ${!item.isCurrentMonth ? 'empty' : ''}
                                                ${selectedDate === item.date ? 'selected' : ''}
                                                ${alreadyAdded ? 'holiday-added' : ''}
                                                ${alreadyExist ? 'holiday-exist' : ''}
            `}
                                            onClick={() => handleSelectDay(item.date)}
                                        >
                                            {item.day || ''}
                                        </button>
                                    );
                                })}
                            </div>

                            {selectedDate && (
                                <div className="holiday-selected-date">
                                    <Input
                                        placeholder={t('bslbr:translation.HolidayDescription')}
                                        label={t('bslbr:translation.HolidayDescription')}
                                        value={holidayDescription}
                                        onChange={(value: any) =>
                                            setHolidayDescription(
                                                typeof value === 'string' ? value : (value?.target?.value ?? '')
                                            )
                                        }
                                    />

                                    <div className="holiday-selected-date-actions">
                                        <Button
                                            type="primary"
                                            content={t('common:actions.Add')}
                                            iconPosition="right"
                                            disabled={!holidayDescription.trim()}
                                            onClick={handleAddHoliday}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="holiday-list-card">
                        <div className="holiday-list-header">
                            <h2>{t('bslbr:translation.HolidaysAdded')}</h2>
                            <span>{holidays.length}</span>
                        </div>

                        {holidays.length === 0 ? (
                            <div className="holiday-empty-list">{t('bslbr:translation.NoHolidaysAdded')}</div>
                        ) : (
                            <div className="holiday-list">
                                {holidays.map((holiday, index) => (
                                    <div className="holiday-list-item" key={`${holiday.Country}-${holiday.Data}-${index}`}>
                                        <div>
                                            <strong>{holiday.Description}</strong>
                                            <p>{holiday.Data}</p>
                                            <small>{holiday.Country}</small>
                                        </div>

                                        <Button
                                            type="secondary"
                                            content={t('common:actions.Delete')}
                                            onClick={() =>
                                                setHolidays(current => current.filter((_, itemIndex) => itemIndex !== index))
                                            }
                                            disabled={loadingGetAllHolidays || loadingCreating}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="buttonsHolidays">
                    <Button
                        type="primary"
                        content={t('common:actions.Save')}
                        iconPosition="right"
                        disabled={holidays.length === 0 || loadingGetAllHolidays || loadingCreating}
                        onClick={() => {
                            saveHoliday();
                        }}
                        loading={loadingGetAllHolidays || loadingCreating}
                    />

                    <Button
                        type="secondary"
                        content={t('common:actions.Back')}
                        iconPosition="right"
                        onClick={onBack}
                        disabled={loadingCreating || loadingGetAllHolidays}
                        loading={loadingCreating || loadingGetAllHolidays}
                    />
                </div>
            </div>
        );
    };
    return <> {calendarHoliday()}</>;
};

export default AddHoliday;
